import { useEffect } from 'react';

import { addRequestInterceptor, addResponseInterceptor, isApiError, PathSchema, StatusSchema } from '@/js/libs/api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNavigate, type NavigateFunction } from '@/js/libs/router';

import { onTranslationListOrAddMissing } from 'src/api.ts';
import { useForcedLogout } from 'src/hooks/useForcedLogout.ts';

const isGetTokenError = (error: unknown) => isApiError(error, PathSchema) && error.response?.data.path === '/api/token';

const isGetTokenRequest = (url: string) => url === '/api/ws-companies/token';

type UseAxiosIntegrationParams = {
  headers: Record<string, string>;
};

export const useAxiosIntegration = ({ headers }: UseAxiosIntegrationParams) => {
  const onForcedLogout = useForcedLogout();
  const navigate = useCallbackRef<NavigateFunction>(useNavigate());
  // The interceptors are registered once and read the headers through this callback ref, which
  // React refreshes before any layout or passive effect of a commit, so every request carries the
  // current headers.
  const getHeaders = useCallbackRef(() => headers);

  useEffect(() => {
    const removeHeadersInterceptor = addRequestInterceptor({
      async onFulfilled(request) {
        const currentHeaders = getHeaders();
        if (onTranslationListOrAddMissing(request.url) || isGetTokenRequest(request.url)) {
          // remove Authorization header for translations and auth requests
          const { Authorization: _Authorization, ...headersWithoutAuthorization } = currentHeaders;
          request.headers = {
            ...request.headers,
            ...headersWithoutAuthorization,
          };
        } else {
          request.headers = {
            ...request.headers,
            ...currentHeaders,
          };
        }
        return request;
      },
    });

    const removeErrorStatusInterceptor = addResponseInterceptor({
      onRejected(error) {
        if (isApiError(error, StatusSchema) && error.response) {
          if (error.response.status === 401 && !isGetTokenError(error)) {
            onForcedLogout();
          }

          if (error.response.status === 403 && window.location.pathname !== '/403' && window.location.pathname !== '/maintenance') {
            navigate('/403');
          }

          if (error.response.status === 404 && window.location.pathname !== '/404') {
            navigate('/404');
          }
        }
      },
    });

    return () => {
      removeHeadersInterceptor();
      removeErrorStatusInterceptor();
    };
  }, [getHeaders, navigate, onForcedLogout]);
};
